import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BUSINESS, CATEGORIES, shopCategoryUrl } from "@/lib/site";
import { AREAS_BY_STATE, areaPath } from "@/lib/areas";

const POPULAR = [...AREAS_BY_STATE["washington-dc"]].sort((a, b) => a.mi - b.mi).slice(0, 6);
import { track } from "@/lib/analytics";

const Col = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">{title}</p>
    <ul className="mt-5 space-y-3 text-sm text-neutral-300">{children}</ul>
  </div>
);

const FLink = ({ to, children, testId }) => (
  <li><Link to={to} data-testid={testId} className="transition-colors hover:text-white">{children}</Link></li>
);

export const Footer = () => (
  <footer className="on-dark grain relative bg-brand-ink pb-28 pt-16 text-white lg:pb-10" data-testid="site-footer">
    <div className="dc-stripes absolute inset-x-0 top-0" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo light className="h-14" />
          <address className="mt-8 space-y-3 text-sm not-italic text-neutral-300">
            <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("directions_click", { location: "footer" })} data-testid="footer-address-link" className="flex gap-3 hover:text-white">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />{BUSINESS.addressLine}
            </a>
            <a href={BUSINESS.phoneHref} onClick={() => track("phone_click", { location: "footer" })} data-testid="footer-phone-link" className="flex gap-3 hover:text-white">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />{BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} onClick={() => track("email_click", { location: "footer" })} data-testid="footer-email-link" className="flex gap-3 break-all hover:text-white">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />{BUSINESS.email}
            </a>
            <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-google-link" className="flex gap-3 hover:text-white">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />Find us on Google Maps
            </a>
            <a href={BUSINESS.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="flex gap-3 hover:text-white">
              <Instagram className="mt-0.5 h-4 w-4 shrink-0 text-brand-lime" />@districtgaslightdc
            </a>
          </address>
        </div>
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-4 lg:col-span-8">
          <Col title="Shop">
            <FLink to="/shop-all" testId="footer-shop-all-link">Full menu</FLink>
            {CATEGORIES.slice(0, 4).map((c) => <FLink key={c.slug} to={shopCategoryUrl(c.slug)} testId={`footer-cat-${c.slug}-link`}>{c.name}</FLink>)}
          </Col>
          <Col title="Visit">
            <FLink to="/location" testId="footer-location-link">Location &amp; hours</FLink>
            <FLink to="/areas-we-serve" testId="footer-areas-link">Areas we serve</FLink>
            <FLink to="/medical-card" testId="footer-card-link">Medical card</FLink>
            <FLink to="/about" testId="footer-about-link">About us</FLink>
            <FLink to="/faq" testId="footer-faq-link">FAQ</FLink>
            <li className="pt-2 text-white">{BUSINESS.hoursShort}</li>
          </Col>
          <Col title="Areas we serve">
            {POPULAR.map((a) => <FLink key={a.slug} to={areaPath(a)} testId={`footer-area-${a.slug}`}>Dispensary near {a.name}</FLink>)}
            <FLink to="/areas-we-serve/maryland" testId="footer-area-md">Maryland visitors</FLink>
            <FLink to="/areas-we-serve/virginia" testId="footer-area-va">Virginia visitors</FLink>
            <FLink to="/areas-we-serve" testId="footer-area-all">All areas</FLink>
          </Col>
          <Col title="Legal">
            <FLink to="/legal" testId="footer-legal-link">Privacy &amp; terms</FLink>
            <FLink to="/legal#compliance" testId="footer-compliance-link">Compliance</FLink>
            <li><a href="/sitemap.xml" className="hover:text-white" data-testid="footer-sitemap-link">Sitemap</a></li>
          </Col>
        </div>
      </div>
      <div className="mt-14 border-t border-white/10 pt-8 text-xs leading-relaxed text-neutral-400">
        <p className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="footer-profiles">
          <span className="font-bold uppercase tracking-[0.2em] text-brand-lime">Find us on</span>
          {BUSINESS.profiles.map((p) => (
            <a key={p.label} href={p.href} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-neutral-200 hover:text-white" data-testid={`footer-profile-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>{p.label}</a>
          ))}
        </p>
        <p className="max-w-4xl">
          District Gaslight is an ABCA-licensed medical cannabis retailer in Washington, DC (ABCA Registration No. ABCA-126785). This website is intended for adults 21 and older.
          Purchases require valid government-issued ID and a qualifying medical cannabis patient registration. Information on this site is not medical advice.
          Keep cannabis products out of reach of children and pets.
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} District Gaslight. All rights reserved.</p>
          <p data-testid="footer-credit">
            Website &amp; SEO by{" "}
            <a href="https://webnehtech.com" target="_blank" rel="noopener" className="font-semibold text-neutral-200 underline-offset-4 hover:text-white hover:underline" data-testid="footer-credit-link">webnehtech.com</a>
          </p>
        </div>
      </div>
    </div>
  </footer>
);
